import { graphql } from ':src/graphql';

export const sessionsForCSVExportQuery = graphql(`
  query SessionsForCSVExportQuery(
    $limit: Int!
    $offset: Int!
    $sort: Sort
    $beforeDate: Date
    $afterDate: Date
    $sessionTypeId: Int
    $sessionDispositionIds: [Int]
    $organizationId: Int
    $userId: Int
    $noShowOnly: Boolean
    $programId: String
    $cancelledWithNoticeFilter: CancelledWithNoticeFilter
    $supervisorType: SupervisorTypeFilter
  ) {
    sessions(
      limit: $limit
      offset: $offset
      sort: $sort
      beforeDate: $beforeDate
      afterDate: $afterDate
      sessionTypeId: $sessionTypeId
      sessionDispositionIds: $sessionDispositionIds
      userId: $userId
      organizationId: $organizationId
      noShowOnly: $noShowOnly
      programId: $programId
      cancelledWithNoticeFilter: $cancelledWithNoticeFilter
      supervisorType: $supervisorType
    ) {
      result {
        id
        scheduledAt
        duration
        sessionType {
          id
          displayName
        }
        supervisor {
          id
          firstName
          lastName
          legalFullName: fullName(legal: true)
        }
        sessionAttendees {
          attended
          user {
            id
            firstName
            lastName
            legalFullName: fullName(legal: true)
            email
            organizationUser(deletedForOrganizationId: $organizationId) {
              id
              organizationProgram {
                id
                name
              }
            }
          }
        }
      }
      total
    }
  }
`);

export const sessionsQuery = graphql(`
  query SessionsQuery(
    $limit: Int!
    $offset: Int!
    $sort: Sort
    $beforeDate: Date
    $afterDate: Date
    $sessionTypeId: Int
    $sessionDispositionIds: [Int]
    $organizationId: Int
    $userId: Int
    $noShowOnly: Boolean
    $programId: String
    $forBillingSummaryCSV: Boolean
    $cancelledWithNoticeFilter: CancelledWithNoticeFilter
    $supervisorType: SupervisorTypeFilter
  ) {
    sessions(
      limit: $limit
      offset: $offset
      sort: $sort
      beforeDate: $beforeDate
      afterDate: $afterDate
      sessionTypeId: $sessionTypeId
      sessionDispositionIds: $sessionDispositionIds
      userId: $userId
      organizationId: $organizationId
      noShowOnly: $noShowOnly
      programId: $programId
      forBillingSummaryCSV: $forBillingSummaryCSV
      cancelledWithNoticeFilter: $cancelledWithNoticeFilter
      supervisorType: $supervisorType
    ) {
      result {
        id
        scheduledAt
        sessionType {
          id
          displayName
        }
        duration
        createdBy
        supervisor {
          id
          slug
          firstName
          lastName
          fullName
          mediumAvatar
          hasActiveOrganizationMatch(organizationId: $organizationId)
        }
        sessionAttendees {
          user {
            id
            firstName
            lastName
            fullName
            mediumAvatar
            organizationUser(deletedForOrganizationId: $organizationId) {
              id
              deletedAt
              organizationProgram {
                id
                name
              }
            }
          }
          attended
          billable
          noShow
          partialAttendanceMinutes
        }
        sessionDispositionId
        organizationSession {
          id
          internal
          organization {
            id
            name
            organizationUsers {
              id
              user {
                id
                fullName
                mediumAvatar
                email
              }
              deletedAt
            }
          }
        }
        sessionNotes {
          id
          question
          answer
        }
        sessionNotesInfo {
          id
          clinicalTopics {
            id
            name
          }
          clinicalTopicsOther
          clinicalSkills {
            id
            name
          }
          clinicalSkillsOther
          clinicalPracticeApproaches {
            id
            name
          }
          clinicalPracticeApproachesOther
          clinicalPracticeApproachesNotApplicable
          professionalDevelopmentTools {
            id
            name
          }
          professionalDevelopmentToolsOther
          professionalDevelopmentToolsNotApplicable
          additionalFeedback
        }
        cancelledAt
        cancelledBy {
          id
          fullName
        }
        unavailableParty {
          id
          fullName
        }
        sessionCancellationReason {
          id
          name
        }
        cancellationReason
      }
      total
    }
  }
`);

export const organizationByIdQuery = graphql(`
  query OrganizationByIdQuery($id: Int!) {
    organizationById(id: $id) {
      id
      name
      canRemoveSupervisees
      organizationUsers {
        id
        user {
          fullName
          id
        }
      }
    }
  }
`);

export const upcomingSessionsQuery = graphql(`
  query UpcomingSessionsQuery(
    $limit: Int!
    $organizationId: Int
    $programId: String
    $isTodayOnly: Boolean
  ) {
    upcomingSessions(
      limit: $limit
      organizationId: $organizationId
      programId: $programId
      isTodayOnly: $isTodayOnly
    ) {
      result {
        id
        scheduledAt
        sessionType {
          displayName
        }
        duration
        supervisor {
          id
          fullName
          mediumAvatar
        }
        sessionDispositionId
        sessionAttendees {
          user {
            id
            fullName
            mediumAvatar
          }
          attended
          partialAttendanceMinutes
        }
        sessionNotes {
          id
          question
          answer
        }
      }
    }
  }
`);

export const organizationSummaryByIdQuery = graphql(`
  query OrganizationSummaryByIdQuery($organizationId: Int!, $programId: String) {
    organizationSummaryById(organizationId: $organizationId, programId: $programId) {
      userCount
      pendingUserCount
      inactiveUserCount
      totalMinutes
      supervisorCount
      attendancePercentage
      pendingEmployerIntroCalls {
        id
      }
    }
  }
`);

export const userByIdQuery = graphql(`
  query UserByIdQuery($id: Int!, $deletedForOrganizationId: Int) {
    userById(id: $id) {
      id
      smallAvatar
      mediumAvatar
      firstName
      lastName
      avatarUrl
      fullName
      usersLicenses {
        license {
          name
        }
        usState {
          abbreviation
        }
      }
      organizationSuperviseeOnboardingRequest {
        usState {
          id
          name
          abbreviation
        }
        licenseCategory {
          id
          name
          displayName
          abbreviation
        }
      }
      organizationUser(deletedForOrganizationId: $deletedForOrganizationId) {
        id
        deletedAt
        scheduledForRemovalOn
      }
    }
  }
`);

export const sessionsSummaryByIdQuery = graphql(`
  query SessionsSummaryById($userId: Int) {
    superviseeSessionsSummaryById(userId: $userId) {
      totalMinutes
      individualMinutes
      dyadMinutes
      groupMinutes
    }
  }
`);
